import { useState, useEffect, useRef } from "react";
import CreditCardPaymentButton from "@/components/paymentMethods/creditCard/CreditCardPaymentButton";
import SavedCardButton from "@/components/paymentMethods/SavedCardButton";
import PayPalButton from "@/components/paymentMethods/PayPalButton";
import { Skeleton } from "@/components/ui/skeleton";
import type { OneClickPaymentFooterProps } from "@/interfaces";
import { ExpressCheckoutElement } from "@stripe/react-stripe-js";
import OptimisticGooglePayButton from "@/components/paymentMethods/OptimisticGooglePayButton";
import { isAndroidOrChrome } from "@/lib/browser-utils";
import { isPayPalVaultedEnabled } from "@/lib/game-config-utils";
import { processPayment, getPaymentMethodDefinitions, getCheckoutSession } from "@/services/PaymentService";
import type { ChargeRequest, CheckoutSessionData } from "@/interfaces";
import { useStyleTheme } from "@/hooks/useStyleTheme";
import { useAnalytics } from "@/hooks/useAnalytics";

const OneClickPaymentFooter = ({
  isLoading,
  onConfirm,
  onAddNewCard,
  savedCards,
  selectedCardId,
  lastSelectedCardId,
  handleCardPayClick,
  onSavedCardSelect,
  sessionId,
  gameData,
  isSkeletonPhase = false,
  hasExpressLoadedOnce = false,
  onExpressLoaded,
  isExpressReinitializing = false,
  onExpressReinitialized,
  paypalEnabled = true,
  optimisticGpay = false,
}: OneClickPaymentFooterProps) => {
  const theme = useStyleTheme(gameData);
  const { track } = useAnalytics();
  const [hasExpressPaymentMethods, setHasExpressPaymentMethods] = useState(false);
  const [isExpressLoading, setIsExpressLoading] = useState(true);
  const [loadingVisible, setLoadingVisible] = useState(true);
  const [almostThereVisible, setAlmostThereVisible] = useState(false);
  const [showOptimisticGpay, setShowOptimisticGpay] = useState(false);
  const [hasJustReinitialized, setHasJustReinitialized] = useState(false);
  const [savedCardsReady, setSavedCardsReady] = useState(false);
  const [showGpayUnavailableMessage, setShowGpayUnavailableMessage] = useState(false);
  const [isPayPalLoading, setIsPayPalLoading] = useState(false);
  const prevIsExpressReinitializingRef = useRef(isExpressReinitializing);

  // Extract saved PayPal method (vaulted) - only show PayPal button if this exists
  const savedPayPalMethod = isPayPalVaultedEnabled(gameData.game_id) && savedCards.length > 0
    ? savedCards.find(card => card.id.startsWith('paypal_vaulted_'))
    : undefined;
  
  const shouldShowPayPalButton = paypalEnabled && !!savedPayPalMethod;

  // Handle PayPal vaulted payment - copied from SavedPayPalButton
  const handlePayPalPayment = async () => {
    if (!savedPayPalMethod || isPayPalLoading) return;

    try {
      setIsPayPalLoading(true);
      
      // Create success and cancellation redirect URLs
      const currentUrl = new URL(window.location.href);
      
      // Create success URL that goes directly to /return
      const returnUrl = new URL(`${currentUrl.origin}/return`);
      returnUrl.searchParams.set('pi', sessionId);
      returnUrl.searchParams.set('success', 'true');
      returnUrl.searchParams.set('payment_method', 'paypal_vaulted');
      
      const successUrl = returnUrl.toString();
      const cancelUrl = `${currentUrl.toString()}${currentUrl.search ? '&' : '?'}canceled=true&payment_method=paypal_vaulted`;

      // Call charge API with existing PayPal vaulted payment method
      const chargeRequest: ChargeRequest = {
        session_id: sessionId,
        save_payment_method: false, // Already saved
        is_new_payment_method: false, // Using existing method
        payment_method: {
          id: savedPayPalMethod.id // Use the saved payment method ID
        },
        success_redirect_url: successUrl,
        cancellation_redirect_url: cancelUrl
      };

      let response;
      try {
        response = await processPayment(chargeRequest);
      } catch (error) {
        throw error;
      }

      if (response.result.is_success) {
        // Handle success - redirect or show success message
        if (response.next_actions.length > 0) {
          for (const action of response.next_actions) {
            if (action.action === 'open_url' && action.parameters?.url) {
              window.location.href = action.parameters.url as string;
              return;
            }
          }
        }
        window.location.href = successUrl;
      } else {
        // Handle next actions (like re-authentication)
        if (response.next_actions.length > 0) {
          for (const action of response.next_actions) {
            if (action.action === 'reauth_paypal_vaulted') {
              // PayPal token expired, need to re-authenticate
              // Create special URLs for re-authentication that won't trigger success page
              const reauthReturnUrl = new URL(`${currentUrl.origin}/return`);
              reauthReturnUrl.searchParams.set('pi', sessionId);
              reauthReturnUrl.searchParams.set('paypal_status', 'reauth_success');
              reauthReturnUrl.searchParams.set('payment_method', 'paypal_vaulted');
              
              const reauthCancelUrl = `${currentUrl.toString()}${currentUrl.search ? '&' : '?'}canceled=true&payment_method=paypal_reauth`;
              
              // Fetch payment method definitions to get the correct provider config
              const checkoutSession = await getCheckoutSession(sessionId) as CheckoutSessionData;
              const currency = checkoutSession?.order?.currency;
              const definitions = await getPaymentMethodDefinitions(currency);
              
              // Find the PayPal vaulted definition
              const paypalVaultedDefinition = definitions.definitions.find(def => def.type === 'paypal-vaulted');
              
              if (!paypalVaultedDefinition || !paypalVaultedDefinition.providers || paypalVaultedDefinition.providers.length === 0) {
                throw new Error('PayPal vaulted payment method not available');
              }
              
              // Get the provider config from the definition
              const providerConfig = paypalVaultedDefinition.providers[0];
              
              // This should trigger a new PayPal vaulted setup flow
              const setupChargeRequest: ChargeRequest = {
                session_id: sessionId,
                save_payment_method: true,
                is_new_payment_method: true,
                payment_method: {
                  data: {
                    provider: providerConfig, // Use the exact provider config from definitions
                    description: 'Game Purchase',
                    brand_name: 'Game Store'
                  }
                },
                success_redirect_url: reauthReturnUrl.toString(),
                cancellation_redirect_url: reauthCancelUrl
              };
              
              const setupResponse = await processPayment(setupChargeRequest);
              
              if (setupResponse.next_actions.length > 0) {
                for (const setupAction of setupResponse.next_actions) {
                  if (setupAction.action === 'open_url' && setupAction.parameters?.url) {
                    window.location.href = setupAction.parameters.url as string;
                    return;
                  }
                }
              }
              return;
            } else if (action.action === 'open_url' && action.parameters?.url) {
              window.location.href = action.parameters.url as string;
              return;
            }
          }
        }
        
        throw new Error(response.result.errors.join(', ') || 'PayPal payment failed');
      }
    } catch (error) {
      console.error('PayPal vaulted payment error:', error);
      alert('PayPal payment failed. Please try again.');
    } finally {
      setIsPayPalLoading(false);
    }
  };

  // Track when we transition from/to reinitializing
  useEffect(() => {
    const wasReinitializing = prevIsExpressReinitializingRef.current;
    const isNowReinitializing = isExpressReinitializing;
    const isNowNotReinitializing = !isExpressReinitializing;
    
    // When we START reinitializing, reset loading state
    if (!wasReinitializing && isNowReinitializing && hasExpressLoadedOnce) {
      setIsExpressLoading(true); // Reset to allow the element to load again
    }
    
    // When we FINISH reinitializing, set flag temporarily
    if (wasReinitializing && isNowNotReinitializing && hasExpressLoadedOnce) {
      setHasJustReinitialized(true);
      
      // Clear the flag after the element has had time to load
      const timer = setTimeout(() => {
        setHasJustReinitialized(false);
      }, 2000); // Give it 2 seconds to fully load and update hasExpressPaymentMethods
      
      return () => clearTimeout(timer);
    }
    
    // Update ref for next render
    prevIsExpressReinitializingRef.current = isExpressReinitializing;
  }, [isExpressReinitializing, hasExpressLoadedOnce]);

  // Track when saved cards are ready to prevent "Add New Card" flash
  useEffect(() => {
    // Mark saved cards as ready once skeleton phase is done AND enough time has passed
    // This prevents showing "Add New Card" briefly before saved cards populate
    if (!isSkeletonPhase && !savedCardsReady) {
      // Wait for savedCards async fetch to complete
      // This delay should be long enough for the API call to finish
      const timer = setTimeout(() => {
        setSavedCardsReady(true);
      }, 200); // 200ms delay to ensure savedCards array is fully populated
      return () => clearTimeout(timer);
    }
  }, [isSkeletonPhase, savedCardsReady]);

  // Check if optimistic GPay should be shown and if we're on a supported browser
  const isAndroidChrome = isAndroidOrChrome();
  const optimisticGpaySupported = optimisticGpay && isAndroidChrome;
  
  useEffect(() => {
    const shouldShow = optimisticGpaySupported && !hasExpressLoadedOnce;
    
    if (shouldShow) {
      setShowOptimisticGpay(true);
    }
  }, [optimisticGpaySupported, hasExpressLoadedOnce]);

  // Hide optimistic button when real button loads AND is ready to display
  useEffect(() => {
    if (hasExpressLoadedOnce && !isExpressLoading && showOptimisticGpay) {
      // Only hide if express payment methods are available (smooth transition to real button)
      if (hasExpressPaymentMethods) {
        // Wait for Stripe's button to complete all its internal transitions
        // before revealing it (border → text → final state with card info)
        const transitionTimer = setTimeout(() => {
          setShowOptimisticGpay(false);
        }, 1500);
        return () => clearTimeout(transitionTimer);
      }
      // If no express payment methods, show unavailable message then hide
      else {
        // First, show the unavailable message
        setShowGpayUnavailableMessage(true);
        
        // After showing the message for 1.5 seconds, hide the optimistic button
        const hideTimer = setTimeout(() => {
          setShowOptimisticGpay(false);
          setShowGpayUnavailableMessage(false);
        }, 1500);
        
        return () => clearTimeout(hideTimer);
      }
    }
  }, [hasExpressLoadedOnce, isExpressLoading, showOptimisticGpay, hasExpressPaymentMethods]);

  // Sequential fade: Loading fades to background color, then Almost there fades from background color
  // Use faster timing on iOS/Mac since we're just waiting for saved cards (no optimistic GPay)
  useEffect(() => {
    // Timing based on what we're waiting for:
    // - Android/Chrome with optimistic GPay: Slower animation (waiting for Stripe + optimistic button)
    // - iOS/Mac: Faster animation (just waiting for saved cards to load)
    const fadeOutDelay = optimisticGpaySupported ? 1500 : 300;  // 1.5s vs 0.3s
    const fadeInDelay = optimisticGpaySupported ? 800 : 200;    // 0.8s vs 0.2s
    
    const fadeOutTimer = setTimeout(() => {
      // Start fading out Loading to background color
      setLoadingVisible(false);
      
      // After Loading completely fades to background, start fading in Almost there from background
      const fadeInTimer = setTimeout(() => {
        setAlmostThereVisible(true);
      }, fadeInDelay); // Wait for fade out to complete
      
      return () => clearTimeout(fadeInTimer);
    }, fadeOutDelay);

    return () => clearTimeout(fadeOutTimer);
  }, [optimisticGpaySupported]);

  // Fallback timeout: If ExpressCheckoutElement doesn't load within 10 seconds, hide the loading state
  useEffect(() => {
    if (!isSkeletonPhase && !hasExpressLoadedOnce && isExpressLoading) {
      const fallbackTimer = setTimeout(() => {
        console.warn('ExpressCheckoutElement onReady timeout - forcing load complete');
        setIsExpressLoading(false);
        onExpressLoaded?.();
      }, 10000); // 10 second timeout

      return () => clearTimeout(fallbackTimer);
    }
  }, [isSkeletonPhase, hasExpressLoadedOnce, isExpressLoading, onExpressLoaded]);

  return (
    <div className="flex flex-col gap-2 mt-auto">
      {/* Phase 1: Show skeleton during initial skeleton phase OR when reinitializing */}
      {(isSkeletonPhase || isExpressReinitializing) && (
        <Skeleton className="h-[48px] w-full" />
      )}
      
      {/* Phase 2: Show "Loading..." / "Almost there..." OR Optimistic GPay while waiting for buttons to be ready */}
      {(() => {
        // Show Phase 2 while we're waiting for ExpressCheckoutElement OR saved cards to load
        // This prevents flash of "Add New Card" changing to "Pay with saved card"
        // On Android/Chrome with optimistic GPay: Show the longer animation or optimistic button
        // On iOS/Mac: Show brief loading until cards are ready
        const shouldShowPhase2 = !isSkeletonPhase && !isExpressReinitializing && !showOptimisticGpay && !hasExpressLoadedOnce && (
          (optimisticGpaySupported && (isExpressLoading || !savedCardsReady)) || // Android/Chrome: wait for both
          (!optimisticGpaySupported && !savedCardsReady) // iOS/Mac: just wait for cards
        );
        
        return shouldShowPhase2 && (
        <div className="h-[48px] w-full rounded-md flex items-center justify-center cursor-not-allowed relative" style={{ backgroundColor: '#D5D5D5' }}>
          {/* Loading text */}
          <span 
            className={`text-sm font-medium absolute ${loadingVisible ? 'animate-pulse' : ''}`}
            style={{ 
              color: loadingVisible ? '#212121' : '#D5D5D5',
              transition: 'color 800ms ease-out'
            }}
          >
            Loading...
          </span>
          
          {/* Almost there text */}
          <span 
            className={`text-sm font-medium absolute ${almostThereVisible ? 'animate-pulse' : ''}`}
            style={{ 
              color: almostThereVisible ? '#212121' : '#D5D5D5',
              opacity: almostThereVisible ? 1 : 0,
              transition: almostThereVisible 
                ? 'color 800ms ease-in, opacity 0ms' 
                : 'opacity 0ms'
            }}
          >
            Almost there...
          </span>
        </div>
        );
      })()}
      
      {/* Single ExpressCheckoutElement - position changes based on layout */}
      {(() => {
        // Check if Phase 2 is currently showing
        const isPhase2Active = !isSkeletonPhase && !isExpressReinitializing && !showOptimisticGpay && !hasExpressLoadedOnce && (
          (optimisticGpaySupported && (isExpressLoading || !savedCardsReady)) ||
          (!optimisticGpaySupported && !savedCardsReady)
        );
        
        // Render Phase 3 when:
        // - First load with saved cards: Wait for savedCardsReady AND Express to load (prevents flash)
        // - First load with NO saved cards: Still wait for savedCardsReady to avoid overlap with Phase 2
        // - Subsequent loads (hasExpressLoadedOnce): Render immediately (skip waits on remount)
        // - iOS/Mac: Wait for savedCardsReady to complete Phase 2 loading
        const shouldRenderPhase3 = !isSkeletonPhase && !isPhase2Active && (
          (savedCardsReady || hasExpressLoadedOnce) && // Always wait for cards ready (even if no cards)
          (!isExpressLoading || showOptimisticGpay || hasJustReinitialized || isExpressReinitializing || !optimisticGpaySupported || hasExpressLoadedOnce)
        );
        
        return (
          <>
            {/* Phase 3: Express/Optimistic Google Pay and PayPal section */}
            {/* Always mount ExpressCheckoutElement in Phase 3 to allow onReady to fire */}
            {shouldRenderPhase3 && (
              <>
                {/* Express Checkout container - visibility controlled by state, element always mounted */}
                <div 
                  className={`flex gap-2 ${(showOptimisticGpay || hasExpressPaymentMethods) && shouldShowPayPalButton ? 'flex-row' : 'flex-col'} ${isExpressReinitializing ? 'invisible h-0 overflow-hidden' : ''}`}
                  style={{
                    // Only show when we have content to display
                    display: (showOptimisticGpay || hasExpressPaymentMethods || shouldShowPayPalButton) ? 'flex' : 'none',
                  }}
                >
                  {/* Express/Optimistic Google Pay - shown when available */}
                  {(showOptimisticGpay || hasExpressPaymentMethods || hasJustReinitialized) && (
                    <div 
                      className={`${shouldShowPayPalButton ? 'flex-1' : 'w-full'} relative`}
                      style={{
                        height: showOptimisticGpay ? '48px' : 'auto',
                      }}
                    >
                      {/* Optimistic button - shown on top while real button loads */}
                      {showOptimisticGpay && (
                        <div className="absolute inset-0 z-10">
                          <OptimisticGooglePayButton showUnavailableMessage={showGpayUnavailableMessage} />
                        </div>
                      )}
                      {/* Real Express Checkout - loads invisibly when optimistic is showing */}
                      <div 
                        className="w-full"
                        style={{
                          opacity: showOptimisticGpay ? 0 : 1,
                          transition: 'opacity 300ms ease-in-out',
                          pointerEvents: showOptimisticGpay ? 'none' : 'auto',
                          minHeight: showOptimisticGpay ? '48px' : 'auto',
                        }}
                      >
                        <ExpressCheckoutElement
                          options={{
                            emailRequired: true,
                            paymentMethods: {
                              applePay: "auto",
                              googlePay: "auto",
                              paypal: "never",
                              amazonPay: "never",
                              link: "never",
                              klarna: "never",
                            },
                            paymentMethodOrder: [
                              "apple_pay",
                              "google_pay",
                            ],
                            layout: {
                              maxColumns: 1,
                              overflow: "auto",
                            },
                          }}
                           onClick={(event) => {
                             try {
                               track('click_in_checkout', { 
                                 cta_name: 'pay', 
                                 payment_method: event.expressPaymentType 
                               });
                             } catch {
                               // Ignore tracking errors
                             }
                             event.resolve({});
                           }}
                           onConfirm={onConfirm}
                           onReady={(event) => {
                             try {
                               setIsExpressLoading(false);
                               onExpressLoaded?.();
                               
                               if (isExpressReinitializing) {
                                 onExpressReinitialized?.();
                               }
                               
                               const availablePaymentMethods = event?.availablePaymentMethods;
                               const hasPaymentMethods = !!(availablePaymentMethods && Object.keys(availablePaymentMethods).length > 0);
                               setHasExpressPaymentMethods(hasPaymentMethods);
                             } catch (error) {
                               console.error('Error in ExpressCheckoutElement onReady:', error);
                               setIsExpressLoading(false);
                               onExpressLoaded?.();
                             }
                           }}
                           onLoadError={(error) => {
                             console.error('ExpressCheckoutElement load error:', error);
                             setIsExpressLoading(false);
                             onExpressLoaded?.();
                           }}
                        />
                      </div>
                    </div>
                  )}
                  {/* PayPal button alongside express checkout - only if user has saved vaulted PayPal */}
                  {shouldShowPayPalButton && savedPayPalMethod && (
                    <div className={`${(showOptimisticGpay || hasExpressPaymentMethods) ? 'flex-1' : 'w-full'}`}>
                      <PayPalButton
                        isLoading={isPayPalLoading}
                        isDisabled={isPayPalLoading}
                        onClick={handlePayPalPayment}
                        theme={theme}
                      />
                    </div>
                  )}
                </div>
                
                {/* Hidden ExpressCheckoutElement - always mounted to trigger onReady when no other element is visible */}
                {/* This solves the chicken-and-egg problem on iOS/Mac where hasExpressPaymentMethods is false */}
                {/* until onReady fires, but the element couldn't mount without hasExpressPaymentMethods being true */}
                {!hasExpressPaymentMethods && !showOptimisticGpay && !hasJustReinitialized && (
                  <div style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }}>
                    <ExpressCheckoutElement
                      options={{
                        emailRequired: true,
                        paymentMethods: {
                          applePay: "auto",
                          googlePay: "auto",
                          paypal: "never",
                          amazonPay: "never",
                          link: "never",
                          klarna: "never",
                        },
                        paymentMethodOrder: [
                          "apple_pay",
                          "google_pay",
                        ],
                        layout: {
                          maxColumns: 1,
                          overflow: "auto",
                        },
                      }}
                       onClick={(event) => {
                         try {
                           track('click_in_checkout', { 
                             cta_name: 'pay', 
                             payment_method: event.expressPaymentType 
                           });
                         } catch {
                           // Ignore tracking errors
                         }
                         event.resolve({});
                       }}
                       onConfirm={onConfirm}
                       onReady={(event) => {
                         try {
                           setIsExpressLoading(false);
                           onExpressLoaded?.();
                           
                           const availablePaymentMethods = event?.availablePaymentMethods;
                           const hasPaymentMethods = !!(availablePaymentMethods && Object.keys(availablePaymentMethods).length > 0);
                           
                           // This sets hasExpressPaymentMethods to true, which will unmount this hidden element
                           // and mount the visible ExpressCheckoutElement above
                           setHasExpressPaymentMethods(hasPaymentMethods);
                         } catch (error) {
                           console.error('Error in hidden ExpressCheckoutElement onReady:', error);
                           setIsExpressLoading(false);
                           onExpressLoaded?.();
                         }
                       }}
                       onLoadError={(error) => {
                         console.error('Hidden ExpressCheckoutElement load error:', error);
                         setIsExpressLoading(false);
                         onExpressLoaded?.();
                       }}
                    />
                  </div>
                )}
              </>
            )}
            
            {/* "Or" divider - show when there are payment methods above (including optimistic GPay) */}
            {!isPhase2Active && !isSkeletonPhase && !isExpressReinitializing && 
             (savedCardsReady || hasExpressLoadedOnce) && 
             (!isExpressLoading || showOptimisticGpay || hasJustReinitialized || !optimisticGpaySupported || hasExpressLoadedOnce) && 
             (hasExpressPaymentMethods || shouldShowPayPalButton || showOptimisticGpay) && (
              <div className="flex items-center gap-4 my-2">
                <div className="flex-1 h-[1px] bg-gray-300" />
                <span className="text-sm text-gray-500 font-medium">Or</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
            )}
            
            {/* Card payment button - always show when ready */}
            {!isPhase2Active && !isSkeletonPhase && !isExpressReinitializing && 
             (savedCardsReady || hasExpressLoadedOnce) && 
             (!isExpressLoading || showOptimisticGpay || hasJustReinitialized || !optimisticGpaySupported || hasExpressLoadedOnce) && (
              <div className="md:mb-0 landscape:mb-0 space-y-3">
                {(() => {
                  // Filter out PayPal methods - they're shown in Phase 3
                  const creditCardMethods = savedCards.filter(card => !card.id.startsWith('paypal_vaulted_'));
                  
                  if (creditCardMethods.length > 0) {
                    const cardToShow = creditCardMethods.find(card => 
                      card.id === (lastSelectedCardId || selectedCardId)
                    ) || creditCardMethods[0];
                    
                    if (cardToShow && !selectedCardId) {
                      onSavedCardSelect(cardToShow.id, true);
                    }
                    
                    return (
                      <SavedCardButton
                        cardBrand={cardToShow.card.brand}
                        cardLast4={cardToShow.card.last4}
                        isLoading={isLoading}
                        isDisabled={isLoading}
                        onClick={handleCardPayClick}
                        savedCards={creditCardMethods}
                        selectedCardId={selectedCardId}
                        onCardSelect={(cardId) => onSavedCardSelect(cardId, true)}
                        onAddNewCard={onAddNewCard}
                        theme={theme}
                      />
                    );
                  } else {
                    return (
                      <CreditCardPaymentButton
                        placeHolder="Add New Card"
                        isLoading={isLoading}
                        isDisabled={isLoading}
                        onClick={onAddNewCard}
                        theme={theme}
                      />
                    );
                  }
                })()}
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
};

export default OneClickPaymentFooter;
